/*
 * describe-alert.js -- turn a stored alert condition into a readable sentence.
 *
 * The review's central complaint about the alert surfaces was that you cannot
 * tell what an alert actually watches without decoding database-ish rows. This
 * is the single place that answers "what does this alert do?", and it is used
 * by the sidebar alert list, the alert builder's summary line, and the alert
 * management modal, so those three can never disagree.
 *
 * Input is an `alarm` row as the server sends it (see strategyArray() in
 * views.py). The shapes below are taken from real rows in the production
 * database, not from the model definition -- the two differ in ways that
 * matter:
 *
 *   price      operator '>',  value_type 'Indicator', second_variable 'MA',
 *              second_params '{"param1":200}'      -> price vs an overlay
 *   indicator  alarm_type_variable 'RSI', first_params '{"param1":14}',
 *              operator '>>', value_type 'constant', const_value 30
 *   volume     value_type 'indicator', second_variable 'ma20'
 *   pattern    alarm_type_variable 'CDLADVANCEBLOCK', second_variable 'any'
 *   trendline  first_variable is an overlay id, first_params carries slopes
 *
 * Note `value_type` arrives as 'Indicator', 'indicator', 'constant',
 * 'Constant', '' and null, so every comparison here is case-insensitive and
 * null-tolerant.
 *
 * Published on window like format.js, and for the same reason: Alpine reads it
 * as a global and a global is not reactive, so it must exist before first
 * render. boot/base.js imports it before Alpine.start().
 *
 * TRANSLATION -- why the sentences are not assembled from pieces any more.
 *
 * This module used to build its output by concatenation: 'Price ' + op + ' ' +
 * rhs, with op looked up as the bare phrase 'crosses above'.  That cannot be
 * translated.  Turkish is verb-final and case-marking, so 'Price crosses above
 * 70' is 'Fiyat 70 seviyesini yukari keserse' -- the verb moves to the end and
 * the operand takes a suffix chosen BY the verb.  Translating 'crosses above'
 * on its own has no correct answer, because the phrase does not survive as a
 * unit in the target language.
 *
 * So the unit of translation is a whole sentence, keyed by condition type AND
 * operator ('cond.price.>>'), with the operands as {placeholders} the
 * translator may move anywhere.  ~30 keys instead of 6, in exchange for output
 * that is grammatical in both languages rather than word-correct in one.
 */
import { friendlyLine, hasNamedLines } from './indicator-labels.js';

(function () {
  'use strict';

  /* Operator vocabulary, from the builder's own <option> list in
     alarm-form-line.js. '>' and '>>' are different questions -- "is above"
     is a state, "crosses above" is an event -- and conflating them is exactly
     the kind of thing users cannot verify from a row of fields.

     Normalised to a slug rather than to a phrase, because the phrase is now
     part of a translated sentence and no longer exists on its own. '==' folds
     onto '=' so the two spellings share one key instead of two identical
     entries the translator has to keep consistent by hand. */
  const OPERATORS = {
    '>':  'gt',
    '<':  'lt',
    '=':  'eq',
    '==': 'eq',
    '!=': 'ne',
    '>>': 'xup',
    '<<': 'xdown',
  };

  /* Every operator slug, for the key sweep in _i18n.html. */
  const OPERATOR_SLUGS = ['gt', 'lt', 'eq', 'ne', 'xup', 'xdown', 'cmp'];

  /* window.t when i18n.js has loaded, identity-with-interpolation when it has
     not -- this module is also require()d outside a browser. */
  function T(key, vars) {
    if (typeof window !== 'undefined' && typeof window.t === 'function') {
      return window.t(key, vars);
    }
    return key;
  }

  /* The translated sentence for one condition.

     `type` is the condition kind and `op` the operator slug; an operator the
     table does not know degrades to 'cmp' ("compares to"), which is vague but
     true, rather than to a missing key. */
  function sentence(type, op, vars) {
    return T('cond.' + type + '.' + (op || 'cmp'), vars);
  }

  /* TA-Lib candlestick pattern codes -> the names people use. Only the ones
     the product actually offers; anything else is de-prefixed and title-cased
     so a new pattern degrades to "Advance Block" rather than to a raw code. */
  const PATTERNS = {
    CDLADVANCEBLOCK: 'Advance Block',
    CDLENGULFING: 'Engulfing',
    CDLHAMMER: 'Hammer',
    CDLINVERTEDHAMMER: 'Inverted Hammer',
    CDLMORNINGSTAR: 'Morning Star',
    CDLEVENINGSTAR: 'Evening Star',
    CDLDOJI: 'Doji',
    CDLSHOOTINGSTAR: 'Shooting Star',
    CDLHANGINGMAN: 'Hanging Man',
    CDL3WHITESOLDIERS: 'Three White Soldiers',
    CDL3BLACKCROWS: 'Three Black Crows',
    CDLHARAMI: 'Harami',
    CDLPIERCING: 'Piercing',
    CDLDARKCLOUDCOVER: 'Dark Cloud Cover',
  };

  const lower = (v) => (v === null || v === undefined ? '' : String(v).trim().toLowerCase());
  const has = (v) => v !== null && v !== undefined && String(v).trim() !== '';

  /* format.js when it is present, plain String() when it is not. Keeps this
     module usable outside a browser (tests) and independent of load order. */
  function fmtWith(kind, n) {
    const f = (typeof window !== 'undefined' && window.fmt) ? window.fmt : null;
    return f && f[kind] ? f[kind](n) : String(n);
  }

  function patternName(code) {
    if (!has(code)) return T('pattern.unknown');
    const key = String(code).toUpperCase();
    if (PATTERNS[key]) return PATTERNS[key];
    const bare = key.replace(/^CDL/, '').toLowerCase();
    return bare.charAt(0).toUpperCase() + bare.slice(1);
  }

  /*
   * first_params / second_params arrive as JSON strings like {"param1":14},
   * sometimes as objects, sometimes '' or null. Returns an array of the
   * numeric parameters in order, which is what an indicator label needs.
   */
  function params(raw) {
    if (!has(raw)) return [];
    let obj = raw;
    if (typeof raw === 'string') {
      try { obj = JSON.parse(raw); } catch (e) { return []; }
    }
    if (!obj || typeof obj !== 'object') return [];
    return Object.keys(obj)
      .filter((k) => /^param\d+$/i.test(k))
      .sort()
      .map((k) => obj[k])
      .filter((v) => v !== null && v !== undefined && v !== '');
  }

  /** "RSI" + {"param1":14} -> "RSI(14)"; no params -> "RSI". */
  function indicatorLabel(name, rawParams) {
    if (!has(name)) return '';
    const p = params(rawParams);
    const label = String(name).toUpperCase();
    return p.length ? label + '(' + p.join(', ') + ')' : label;
  }

  /*
   * An indicator's line named in words, so two lines of one indicator can be told
   * apart: "MACD signal line (12, 26, 9)" rather than "MACD(12, 26, 9)" for both
   * sides of a MACD cross. `output` is the specific series (MACD's DIF / DEA /
   * histogram, a band's UP / MID / DN). Indicators with nothing to tell apart keep
   * the short form ("RSI(14)"); an unrecognised series is appended as it is.
   */
  function lineName(name, output, rawParams) {
    const base = indicatorLabel(name, rawParams);
    if (!has(output) || lower(output) === 'undefined') return base;
    if (hasNamedLines(name)) return friendlyLine(name, output, params(rawParams));
    return lower(output) !== lower(name) ? base + ' ' + output : base;
  }

  /*
   * The right-hand side of the comparison. Either a constant, or another
   * indicator/overlay, optionally on a different asset or timeframe -- which
   * is the whole point of the cross-asset and cross-timeframe alerts, and was
   * previously invisible in the UI.
   */
  function rightHandSide(a) {
    const vt = lower(a.value_type);
    /* A constant compared against price IS a price, so it gets price
       precision and thousands separators ("55,000.00", not "55000"). Constants
       compared against an indicator keep indicator precision. */
    const constKind = lower(a.type) === 'price' ? 'price' : 'threshold';

    if (vt === 'constant' || (!has(a.second_variable) && has(a.const_value))) {
      const n = Number(a.const_value);
      if (Number.isFinite(n)) {
        return fmtWith(constKind, n);
      }
    }

    if (has(a.second_variable)) {
      let s = lineName(a.second_variable, a.second_value, a.second_params);
      const parts = [];
      if (has(a.second_security) && lower(a.second_security) !== lower(a.first_security)) {
        parts.push(String(a.second_security).toUpperCase());
      }
      if (has(a.second_timeframe) && lower(a.second_timeframe) !== lower(a.first_timeframe)) {
        parts.push(T('rhs.on_timeframe', { timeframe: a.second_timeframe }));
      }
      if (parts.length) s += ' (' + parts.join(' ') + ')';
      return s;
    }

    if (has(a.const_value)) {
      const n = Number(a.const_value);
      if (Number.isFinite(n)) return fmtWith(constKind, n);
    }
    if (has(a.price)) {
      const n = Number(a.price);
      if (Number.isFinite(n)) return fmtWith('price', n);
    }
    /* Nothing on the right-hand side yet. Say so plainly -- in the builder
       this sentence is how someone checks their own half-built alert, and
       "its threshold" reads as though a value had been set. */
    return T('rhs.unset');
  }

  /* The operator SLUG for a stored operator string. Kept exported under the
     old name because the alert builder calls describe.operatorPhrase() to
     label its own <option>s; it now returns a translated standalone phrase,
     which is the one place a bare operator legitimately appears (a dropdown
     has no sentence around it). */
  function operatorSlug(op) {
    return OPERATORS[String(op || '').trim()] || 'cmp';
  }

  function operatorPhrase(op) {
    return T('operator.' + operatorSlug(op));
  }

  /**
   * One condition as a sentence fragment, e.g.
   *   "Price crosses above MA(200)"
   *   "RSI(14) crosses above 30"
   *   "Volume is above MA20"
   *   "Advance Block pattern appears"
   *   "Price crosses above a trend line you drew"
   *
   * Never returns an empty string: an unrecognised row still yields something
   * a human can read, because a blank row in the list is worse than a vague
   * one.
   */
  /*
   * The same condition reaches this module under two spellings: saved rows use
   * the column names (const_value, first_params), while the live builder form
   * uses its own field names (constant_value, first_parameter). Normalising
   * here keeps one describer for both instead of one that silently produces
   * "its threshold" for every unsaved condition.
   */
  function normalise(a) {
    if (!a) return a;
    const n = Object.assign({}, a);
    if (!has(n.const_value) && has(a.constant_value)) n.const_value = a.constant_value;
    if (!has(n.first_params) && has(a.first_parameter)) n.first_params = a.first_parameter;
    if (!has(n.second_params) && has(a.second_parameter)) n.second_params = a.second_parameter;
    return n;
  }

  function economicLabel(a) {
    if (a.figure_label) return a.figure_label;
    const code = a.first_variable || a.alarm_type_variable;
    const f = (window.ECONOMIC_FIGURES || {})[code];
    if (!f) return code || T('left.this_alert');
    return f.country === 'CRYPTO' ? f.name : f.country + ' ' + f.name;
  }

  function describeEconomic(a) {
    const figure = economicLabel(a);
    const kind = lower(a.value_type) || 'previous';
    let text;
    if (kind === 'event') {
      text = T('cond.economic.released', { figure: figure });
    } else if (kind === 'constant') {
      const code = a.first_variable || a.alarm_type_variable;
      const meta = (window.ECONOMIC_FIGURES || {})[code];
      const rhs = meta && window.fmt ? window.fmt.econ(a.const_value, null, meta) : String(a.const_value);
      text = sentence('economic', operatorSlug(a.operator), { figure: figure, rhs: rhs });
    } else {
      text = sentence('economic_prev', operatorSlug(a.operator), { figure: figure });
    }
    const n = Number(a.duration);
    return n > 1 ? T('cond.economic.window', { condition: text, n: n }) : text;
  }

  function describeCondition(raw) {
    if (!raw) return T('cond.unavailable');
    const a = normalise(raw);
    const type = lower(a.type);

    // An economic release as a condition: "US CPI MoM comes in above the
    // previous figure", "... above 3%", "... is released"; with ", counting
    // for 3 candles" when it stays true beyond the release's own candle.
    if (type === 'economic') return describeEconomic(a);
    const op = operatorSlug(a.operator);

    if (type === 'trendline') {
      return sentence('trendline', op);
    }

    if (type === 'pattern') {
      /* Not operator-keyed: a pattern either appeared or it did not, so there
         is one sentence, with an optional direction qualifier. */
      const name = patternName(a.alarm_type_variable);
      const dir = lower(a.second_variable);
      return (dir && dir !== 'any' && dir !== 'undefined')
        ? T('cond.pattern.directional', { name: name, direction: dir })
        : T('cond.pattern.any', { name: name });
    }

    if (type === 'volume') {
      return sentence('volume', op, { rhs: rightHandSide(a) });
    }

    if (type === 'price') {
      return sentence('price', op, { rhs: rightHandSide(a) });
    }

    if (type === 'indicator') {
      /* first_variable is the specific series of a multi-series indicator. For
         MACD that includes the HISTOGRAM, whose stored name is the same as the
         indicator's own ("MACD"), so it used to read as the MACD line itself. */
      const name = lineName(a.alarm_type_variable || a.first_variable, a.first_variable, a.first_params);
      return sentence('indicator', op,
                      { left: name || T('left.indicator'), rhs: rightHandSide(a) });
    }

    /* Unknown type: still say something truthful using whatever is present. */
    const left = indicatorLabel(a.alarm_type_variable || a.first_variable, a.first_params)
                 || T('left.this_alert');
    return sentence('generic', op, { left: left, rhs: rightHandSide(a) });
  }

  /**
   * The market a condition watches, when it differs from the alert's own
   * market -- cross-asset conditions are otherwise silently misleading.
   */
  function conditionMarket(a, alertAsset, alertTimeframe) {
    if (!a) return '';
    const bits = [];
    if (has(a.first_security) && lower(a.first_security) !== lower(alertAsset)) {
      bits.push(String(a.first_security).toUpperCase());
    }
    if (has(a.first_timeframe) && lower(a.first_timeframe) !== lower(alertTimeframe)) {
      bits.push(a.first_timeframe);
    }
    return bits.join(' · ');
  }

  /**
   * The whole alert as one sentence, for the builder's summary line.
   *
   * Groups of conditions are joined with "and"; conditions inside a group
   * (same group_no) with "or". Each condition can also carry "within the last
   * N candles" and NOT. See joinGroups.
   *
   * The trailing clause states when it is checked. The worker loop only wakes
   * on candle close, so every alert is a candle-close alert; saying so is the
   * point, since the review flagged that this behaviour surprises people.
   */
  /* Join the condition fragments into a list.

     The separator and the final conjunction are both translated, and the
     Oxford comma is a property of the ENGLISH key, not of this function --
     Turkish writes "a, b ve c" with no comma before "ve", so hardcoding one
     here would have put a stray comma in every multi-condition Turkish
     summary. */
  function joinConditions(parts) {
    if (parts.length === 1) return parts[0];
    if (parts.length === 2) {
      return T('list.pair', { first: parts[0], last: parts[1] });
    }
    return T('list.many', {
      items: parts.slice(0, -1).join(T('list.separator')),
      last: parts[parts.length - 1],
    });
  }

  /* Lowercase the first letter of a fragment being spliced mid-sentence.
   *
   * toLocaleLowerCase, not toLowerCase: Turkish capital 'I' lowercases to
   * dotless 'i', not to 'i'. Plain toLowerCase() would render "Ichimoku ..."
   * as "ichimoku ..." in a Turkish sentence, which is a spelling error rather
   * than a style one. The locale comes from the same place the catalog does.
   */
  function lowerFirst(s) {
    if (!s) return s;
    /* Leave acronyms alone: "RSI(14) ..." must not become "rSI(14) ...".
       A second capital letter is the tell. */
    if (s.length > 1 && s[1] === s[1].toUpperCase() && /[A-Z]/.test(s[1])) return s;
    const locale = (typeof window !== 'undefined' && window.t && window.t.locale)
      ? window.t.locale : 'en';
    return s.charAt(0).toLocaleLowerCase(locale) + s.slice(1);
  }

  /* One condition with its logic settings applied: "within the last N
     candles" and NOT, in the order the evaluator applies them (lookback
     first, then negation). Returns a fragment that is safe to splice. */
  function describeWithLogic(cond) {
    const text = lowerFirst(describeCondition(cond));
    const n = Number(cond && cond.lookback);
    const within = n > 1;
    const ago = Number(cond && cond.ago) || 0;
    let when = text;
    if (cond && cond.negate) {
      when = within ? T('cond.not.within', { condition: text, n: n })
                    : T('cond.not', { condition: text });
    } else if (within) {
      when = T('cond.within', { condition: text, n: n });
    }
    return ago > 0 ? T('cond.ago', { condition: when, n: ago }) : when;
  }

  /* The conditions as the evaluator combines them: conditions sharing a
     group_no are ORed, a condition without one is a group of its own, and the
     groups are ANDed. Mirrors crpytview/apps/domain/condition_logic.py. */
  function joinGroups(conds) {
    const groups = [];
    const byNo = {};
    conds.forEach((cond) => {
      const no = cond && cond.group_no;
      const text = describeWithLogic(cond);
      if (no === null || no === undefined || no === '') {
        groups.push([text]);
      } else if (byNo[no]) {
        byNo[no].push(text);
      } else {
        byNo[no] = [text];
        groups.push(byNo[no]);
      }
    });
    const multi = groups.length > 1;
    const parts = groups.map((g) => {
      if (g.length === 1) return g[0];
      const either = g.length === 2
        ? T('list.or.pair', { first: g[0], last: g[1] })
        : T('list.or.many', { items: g.slice(0, -1).join(T('list.separator')), last: g[g.length - 1] });
      return multi ? '(' + either + ')' : either;
    });
    return joinConditions(parts);
  }

  function describeAlert(alert) {
    if (!alert) return '';
    const conds = (alert.conditions || []).filter(Boolean);
    const market = (alert.asset ? String(alert.asset).toUpperCase() : T('market.this'));
    const tf = alert.timeframe || '';

    if (!conds.length) {
      return tf ? T('summary.empty.timeframe', { market: market, timeframe: tf })
                : T('summary.empty', { market: market });
    }

    /* The condition strings are capitalised because they also appear on their
       own as alert-list rows; spliced into the middle of this sentence they
       have to come back down. Both languages need it -- Turkish no less than
       English -- so the fix is a locale-aware lowercase, not dropping the
       step. */
    const joined = joinGroups(conds);

    return tf
      ? T('summary.timeframe', { market: market, timeframe: tf, conditions: joined })
      : T('summary', { market: market, conditions: joined });
  }

  const describe = {
    condition: describeCondition,
    alert: describeAlert,
    market: conditionMarket,
    indicatorLabel: indicatorLabel,
    operatorPhrase: operatorPhrase,
    operatorSlug: operatorSlug,
    operatorSlugs: OPERATOR_SLUGS,
    patternName: patternName,
    params: params,
  };

  window.describe = describe;
})();

// Also reachable as window.describe: Alpine's inline expressions can only see globals.
export const describe = window.describe;
